import React, { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../utils/auth';

const NVIDIA_MODELS = [
    {
        value: "black-forest-labs/flux.1-schnell",
        label: "FLUX.1 Schnell (Black Forest Labs)",
        supportsNegative: false,
        defaultSteps: 4,
        maxSteps: 12,
        note: "Najszybszy model, 4–12 kroków"
    },
    {
        value: "stabilityai/sdxl-turbo",
        label: "SDXL Turbo (Stability AI)",
        supportsNegative: true,
        defaultSteps: 10,
        maxSteps: 50,
        note: "Szybki SDXL, steps ≥ 5"
    },
    {
        value: "stabilityai/stable-diffusion-3-medium",
        label: "Stable Diffusion 3 Medium",
        supportsNegative: true,
        defaultSteps: 28,
        maxSteps: 50,
        note: "Wysoka jakość"
    },
    {
        value: "stabilityai/stable-diffusion-xl-v1_0",
        label: "Stable Diffusion XL 1.0",
        supportsNegative: true,
        defaultSteps: 30,
        maxSteps: 100,
        note: "Standard SDXL"
    },
];

const OMNIGATE_IMAGE_MODELS = [
    {
        value: "omnigate/lexxa-v2.0",
        label: "LEXXAv2.0 (OmniGate Provider)",
        supportsNegative: true,
        note: "Perchance / Lexxa przez MCP 8766 + browser 7901"
    },
    {
        value: "gpt4free-browser-mcp:image_generation",
        label: "GPT4Free Image Generation (MCP 8766)",
        supportsNegative: true,
        note: "GPT4Free przez MCP port 8766"
    },
];

const OMNIGATE_AUDIO_MODELS = [
    { value: "omnigate/text-to-audio", label: "Text To Audio (OmniGate)" },
];

const AUDIO_VOICES = [
    { value: "alloy", label: "Alloy" },
    { value: "echo", label: "Echo" },
    { value: "fable", label: "Fable" },
    { value: "onyx", label: "Onyx" },
    { value: "nova", label: "Nova" },
    { value: "shimmer", label: "Shimmer" },
];

const RESOLUTIONS = [
    { value: "1024x1024", label: "1024 × 1024 (1:1)", w: 1024, h: 1024 },
    { value: "1216x832", label: "1216 × 832 (3:2 landscape)", w: 1216, h: 832 },
    { value: "832x1216", label: "832 × 1216 (2:3 portrait)", w: 832, h: 1216 },
    { value: "1344x768", label: "1344 × 768 (16:9 wide)", w: 1344, h: 768 },
    { value: "768x1344", label: "768 × 1344 (9:16 story)", w: 768, h: 1344 },
    { value: "1152x896", label: "1152 × 896 (4:3 landscape)", w: 1152, h: 896 },
    { value: "896x1152", label: "896 × 1152 (3:4 portrait)", w: 896, h: 1152 },
];

const SAMPLERS = [
    "K_EULER_ANCESTRAL", "K_EULER", "K_DPMPP_2S_ANCESTRAL", "K_DPM_2", "DDIM", "PLMS"
];

const formatModelLabel = (model?: string, meta?: any) => {
    if (meta?.model_label) return meta.model_label;
    if (!model) return "?";
    if (model === "omnigate/lexxa-v2.0") return "LEXXAv2.0";
    if (model === "omnigate/text-to-audio") return "OmniGate Audio";
    return model.split('/').pop() || model;
};

const mediaSrc = (url: string) => url.startsWith('http://') || url.startsWith('https://') ? url : `${getApiBaseUrl()}${url}`;

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const cls: Record<string, string> = {
        processing: "bg-blue-500/20 text-blue-400 animate-pulse",
        queued: "bg-yellow-500/20 text-yellow-400",
        completed: "bg-green-500/20 text-green-400",
        failed: "bg-red-500/20 text-red-400",
    };
    return <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${cls[status] || "bg-slate-700 text-slate-400"}`}>{status}</span>;
};

export const MediaOutputsPanel: React.FC = () => {
    const [mediaItems, setMediaItems] = useState<any[]>([]);
    const [queueTasks, setQueueTasks] = useState<any[]>([]);

    const [nvidiaPrompt, setNvidiaPrompt] = useState("");
    const [nvidiaNegative, setNvidiaNegative] = useState("");
    const [nvidiaModelKey, setNvidiaModelKey] = useState(NVIDIA_MODELS[0].value);
    const [nvidiaResolution, setNvidiaResolution] = useState("1024x1024");
    const [nvidiaSteps, setNvidiaSteps] = useState(4);
    const [nvidiaSeed, setNvidiaSeed] = useState("");
    const [nvidiaGuidance, setNvidiaGuidance] = useState(7.0);
    const [nvidiaSampler, setNvidiaSampler] = useState("K_EULER_ANCESTRAL");
    const [nvidiaBatch, setNvidiaBatch] = useState(1);
    const [nvidiaAdvOpen, setNvidiaAdvOpen] = useState(false);
    const [nvidiaBusy, setNvidiaBusy] = useState(false);

    const [omniMode, setOmniMode] = useState<'image' | 'audio'>('image');
    const [omniPrompt, setOmniPrompt] = useState("");
    const [omniNegative, setOmniNegative] = useState("");
    const [omniImageModel, setOmniImageModel] = useState(OMNIGATE_IMAGE_MODELS[0].value);
    const [omniAudioModel, setOmniAudioModel] = useState(OMNIGATE_AUDIO_MODELS[0].value);
    const [omniResolution, setOmniResolution] = useState("1024x1024");
    const [omniVoice, setOmniVoice] = useState(AUDIO_VOICES[0].value);
    const [omniSendResolution, setOmniSendResolution] = useState(false);
    const [omniBusy, setOmniBusy] = useState(false);
    const [lastError, setLastError] = useState<string | null>(null);

    const selectedNvidiaModel = NVIDIA_MODELS.find(m => m.value === nvidiaModelKey) || NVIDIA_MODELS[0];
    const selectedNvidiaRes = RESOLUTIONS.find(r => r.value === nvidiaResolution) || RESOLUTIONS[0];
    const selectedOmniRes = RESOLUTIONS.find(r => r.value === omniResolution) || RESOLUTIONS[0];
    const isFlux = nvidiaModelKey.startsWith("black-forest-labs/");

    const apiHeaders = () => {
        const apiKey = localStorage.getItem('omnigate_api_key') || '';
        return { 'X-API-Key': apiKey, 'Authorization': `Bearer ${apiKey}` };
    };

    const refresh = async () => {
        try {
            const h = apiHeaders();
            const [qRes, gRes] = await Promise.all([
                fetch('/v1/media/queue', { headers: h }),
                fetch('/v1/media/gallery', { headers: h }),
            ]);
            if (qRes.ok) { const d = await qRes.json(); setQueueTasks(d.tasks || []); }
            if (gRes.ok) { const d = await gRes.json(); setMediaItems(d.items || []); }
        } catch (e) {
            console.error("Media refresh error:", e);
        }
    };

    useEffect(() => {
        refresh();
        const id = setInterval(refresh, 3000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        setNvidiaSteps(selectedNvidiaModel.defaultSteps);
    }, [nvidiaModelKey]);

    const enqueueGenerate = async (body: any, copies: number) => {
        const jobs = Array.from({ length: copies }, () =>
            fetch('/v1/media/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...apiHeaders() },
                body: JSON.stringify(body),
            })
        );
        const results = await Promise.all(jobs);
        const bad = results.find(r => !r.ok);
        if (bad) {
            throw new Error(await bad.text());
        }
        await refresh();
    };

    const handleGenerateNvidia = async () => {
        if (!nvidiaPrompt.trim() || nvidiaBusy) return;
        setLastError(null);
        setNvidiaBusy(true);
        try {
            const params: any = {
                width: selectedNvidiaRes.w,
                height: selectedNvidiaRes.h,
                steps: nvidiaSteps,
                guidance_scale: nvidiaGuidance,
                sampler: nvidiaSampler,
            };
            if (nvidiaSeed.trim()) params.seed = parseInt(nvidiaSeed);
            await enqueueGenerate({
                prompt: nvidiaPrompt,
                negative_prompt: nvidiaNegative,
                model: nvidiaModelKey,
                parameters: params,
            }, nvidiaBatch);
            setNvidiaPrompt("");
            setNvidiaNegative("");
        } catch (e: any) {
            setLastError(`NVIDIA generation error: ${String(e)}`);
        } finally {
            setNvidiaBusy(false);
        }
    };

    const handleGenerateOmni = async () => {
        if (!omniPrompt.trim() || omniBusy) return;
        setLastError(null);
        setOmniBusy(true);
        try {
            if (omniMode === 'image') {
                const params: any = {
                    guidance_scale: 7,
                    seed: -1,
                };
                if (omniSendResolution) {
                    params.width = selectedOmniRes.w;
                    params.height = selectedOmniRes.h;
                }
                await enqueueGenerate({
                    prompt: omniPrompt,
                    negative_prompt: omniNegative,
                    model: omniImageModel,
                    parameters: params,
                }, 1);
                setOmniNegative("");
            } else {
                await enqueueGenerate({
                    prompt: omniPrompt,
                    model: omniAudioModel,
                    parameters: { voice: omniVoice },
                }, 1);
            }
            setOmniPrompt("");
        } catch (e: any) {
            setLastError(`OmniGate generation error: ${String(e)}`);
        } finally {
            setOmniBusy(false);
        }
    };

    const handleSendVision = (url: string) => {
        window.dispatchEvent(new CustomEvent('vision-analyze-image', { detail: { url } }));
    };

    const handleDelete = async (id: string) => {
        if (!confirm("Na pewno usunąć ten plik?")) return;
        try {
            await fetch(`/v1/media/gallery/${id}`, {
                method: 'DELETE',
                headers: apiHeaders()
            });
            await refresh();
        } catch (e) {
            console.error("Delete error:", e);
        }
    };

    const renderMediaCard = (item: any, idx: number) => {
        const src = mediaSrc(item.url);
        return (
            <div key={item.id || idx} className="bg-[#131722] border border-[#2a2e39] rounded-xl overflow-hidden shadow-lg group flex flex-col">
                <div className="aspect-square bg-black relative overflow-hidden flex items-center justify-center">
                    {item.type === 'audio' ? (
                        <div className="w-full h-full p-4 flex flex-col justify-center gap-4 bg-gradient-to-br from-[#182033] to-[#10141d]">
                            <div className="flex items-center gap-3 text-cyan-300">
                                <i className="fas fa-wave-square text-2xl" />
                                <div>
                                    <div className="text-[11px] font-black uppercase tracking-widest">OmniGate Audio</div>
                                    <div className="text-[10px] text-slate-400">{item.meta?.voice || 'alloy'}</div>
                                </div>
                            </div>
                            <audio controls src={src} className="w-full" preload="none" />
                        </div>
                    ) : (
                        <img src={src} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    )}
                    <div className="absolute top-2 right-2">
                        <div className="bg-black/70 backdrop-blur-md text-[8px] font-bold text-pink-400 px-2 py-0.5 rounded">
                            {formatModelLabel(item.model, item.meta)}
                        </div>
                    </div>
                </div>
                <div className="p-2 flex flex-col gap-1 bg-[#1e222d] border-t border-[#2a2e39] flex-1">
                    <p className="text-[10px] text-slate-300 line-clamp-2" title={item.title}>{item.title}</p>
                    <div className="flex gap-1 mt-auto pt-1">
                        {item.type === 'image' ? (
                            <button
                                onClick={() => handleSendVision(src)}
                                title="Wyślij do Vision Analyze"
                                className="flex-1 text-[10px] bg-[#2a2e39] hover:bg-cyan-800/40 px-2 py-1 rounded text-cyan-400 transition-colors flex items-center justify-center gap-1"
                            >
                                <i className="fas fa-eye" /> Vision
                            </button>
                        ) : (
                            <div className="flex-1 text-[10px] bg-[#2a2e39] px-2 py-1 rounded text-cyan-400 flex items-center justify-center gap-1">
                                <i className="fas fa-music" /> Audio
                            </div>
                        )}
                        <a href={src} download className="flex-1 text-[10px] bg-[#2a2e39] hover:bg-pink-800/40 px-2 py-1 rounded text-pink-400 transition-colors flex items-center justify-center gap-1">
                            <i className="fas fa-download" /> DL
                        </a>
                        <button
                            onClick={() => handleDelete(item.id)}
                            title="Usuń plik z galerii"
                            className="flex-1 text-[10px] bg-[#2a2e39] hover:bg-red-800/40 px-2 py-1 rounded text-red-500 transition-colors flex items-center justify-center gap-1"
                        >
                            <i className="fas fa-trash" /> Usuń
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col h-full bg-[#1e222d] border border-[#2a2e39] rounded-xl shadow-2xl overflow-hidden text-slate-300">
            <div className="p-4 border-b border-[#2a2e39] bg-[#131722]/60 shrink-0 flex items-center gap-3">
                <i className="fas fa-photo-film text-pink-500 text-lg" />
                <div>
                    <h2 className="text-sm font-black text-white uppercase tracking-widest">Media Gallery</h2>
                    <p className="text-[10px] text-slate-500">NVIDIA NIM + OmniGate Media dla ludzi, nie dla endpointów</p>
                </div>
            </div>

            <div className="flex flex-col md:flex-row flex-1 min-h-0">
                <div className="w-full md:w-[360px] shrink-0 border-b md:border-b-0 md:border-r border-[#2a2e39] flex flex-col bg-[#1a1e27] overflow-y-auto">
                    <div className="p-4 flex flex-col gap-4">
                        <section className="border border-[#2a2e39] rounded-xl p-3 bg-[#131722]">
                            <div className="flex items-center gap-2 mb-3">
                                <i className="fas fa-wand-magic-sparkles text-pink-400" />
                                <div>
                                    <h3 className="text-[11px] font-black text-white uppercase tracking-widest">NVIDIA NIM</h3>
                                    <p className="text-[9px] text-slate-500">Klasyczne generowanie obrazów</p>
                                </div>
                            </div>

                            <div className="flex flex-col gap-3">
                                <div>
                                    <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">Model</label>
                                    <select value={nvidiaModelKey} onChange={e => setNvidiaModelKey(e.target.value)} className="w-full bg-[#1a1e27] border border-[#2a2e39] rounded-lg p-2 text-xs">
                                        {NVIDIA_MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                    </select>
                                    <p className="text-[9px] text-pink-400/70 mt-0.5">{selectedNvidiaModel.note}</p>
                                </div>

                                <div>
                                    <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">Prompt</label>
                                    <textarea value={nvidiaPrompt} onChange={e => setNvidiaPrompt(e.target.value)} rows={4} className="w-full bg-[#1a1e27] border border-[#2a2e39] rounded-lg p-2 text-xs resize-none" />
                                </div>

                                {selectedNvidiaModel.supportsNegative && (
                                    <div>
                                        <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">Negative Prompt</label>
                                        <textarea value={nvidiaNegative} onChange={e => setNvidiaNegative(e.target.value)} rows={2} className="w-full bg-[#1a1e27] border border-[#2a2e39] rounded-lg p-2 text-xs resize-none" />
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-2">
                                    <div className="col-span-2">
                                        <label className="text-[9px] uppercase font-bold text-slate-500 block mb-1">Rozdzielczość</label>
                                        <select value={nvidiaResolution} onChange={e => setNvidiaResolution(e.target.value)} className="w-full bg-[#1a1e27] border border-[#2a2e39] rounded p-1.5 text-[11px]">
                                            {RESOLUTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-[9px] uppercase font-bold text-slate-500 block mb-1">Steps</label>
                                        <input type="number" value={nvidiaSteps} onChange={e => setNvidiaSteps(Math.max(isFlux ? 1 : 5, Math.min(selectedNvidiaModel.maxSteps, parseInt(e.target.value) || 1)))} min={isFlux ? 1 : 5} max={selectedNvidiaModel.maxSteps} className="w-full bg-[#1a1e27] border border-[#2a2e39] rounded p-1.5 text-[11px]" />
                                    </div>
                                    <div>
                                        <label className="text-[9px] uppercase font-bold text-slate-500 block mb-1">Seed</label>
                                        <input type="number" value={nvidiaSeed} onChange={e => setNvidiaSeed(e.target.value)} placeholder="Losowy" className="w-full bg-[#1a1e27] border border-[#2a2e39] rounded p-1.5 text-[11px]" />
                                    </div>
                                    <div>
                                        <label className="text-[9px] uppercase font-bold text-slate-500 block mb-1">CFG</label>
                                        <input type="number" value={nvidiaGuidance} onChange={e => setNvidiaGuidance(parseFloat(e.target.value) || 7)} step={0.5} min={1} max={30} className="w-full bg-[#1a1e27] border border-[#2a2e39] rounded p-1.5 text-[11px]" />
                                    </div>
                                    <div>
                                        <label className="text-[9px] uppercase font-bold text-slate-500 block mb-1">Batch</label>
                                        <input type="number" value={nvidiaBatch} onChange={e => setNvidiaBatch(Math.max(1, Math.min(8, parseInt(e.target.value) || 1)))} min={1} max={8} className="w-full bg-[#1a1e27] border border-[#2a2e39] rounded p-1.5 text-[11px]" />
                                    </div>
                                </div>

                                <div className="border border-[#2a2e39] rounded-lg overflow-hidden">
                                    <button className="w-full p-2 flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase bg-[#1e222d]" onClick={() => setNvidiaAdvOpen(!nvidiaAdvOpen)}>
                                        <span>Sampler & Advanced</span>
                                        <i className={`fas fa-chevron-${nvidiaAdvOpen ? 'up' : 'down'} text-[9px]`} />
                                    </button>
                                    {nvidiaAdvOpen && (
                                        <div className="p-3 bg-[#131722]">
                                            <label className="text-[9px] uppercase font-bold text-slate-500 block mb-1">Sampler</label>
                                            <select value={nvidiaSampler} onChange={e => setNvidiaSampler(e.target.value)} className="w-full bg-[#1e222d] border border-[#2a2e39] rounded p-1.5 text-[11px]">
                                                {SAMPLERS.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </div>
                                    )}
                                </div>

                                <button onClick={handleGenerateNvidia} disabled={nvidiaBusy || !nvidiaPrompt.trim()} className="w-full bg-gradient-to-r from-pink-600 to-purple-600 disabled:opacity-40 text-white font-black text-[11px] uppercase tracking-widest py-3 rounded-xl flex justify-center items-center gap-2">
                                    {nvidiaBusy ? <><i className="fas fa-spinner fa-spin" /><span>WYSYŁANIE...</span></> : <><i className="fas fa-bolt" /><span>GENERUJ NVIDIA</span></>}
                                </button>
                            </div>
                        </section>

                        <section className="border border-[#2a2e39] rounded-xl p-3 bg-[#131722]">
                            <div className="flex items-center gap-2 mb-3">
                                <i className="fas fa-satellite-dish text-cyan-400" />
                                <div>
                                    <h3 className="text-[11px] font-black text-white uppercase tracking-widest">OmniGate Media</h3>
                                    <p className="text-[9px] text-slate-500">Image + Audio z kontenera</p>
                                </div>
                            </div>

                            <div className="flex gap-2 mb-3">
                                <button onClick={() => setOmniMode('image')} className={`flex-1 rounded-lg px-3 py-2 text-[10px] font-bold uppercase ${omniMode === 'image' ? 'bg-cyan-600 text-white' : 'bg-[#1a1e27] text-slate-400 border border-[#2a2e39]'}`}>Image</button>
                                <button onClick={() => setOmniMode('audio')} className={`flex-1 rounded-lg px-3 py-2 text-[10px] font-bold uppercase ${omniMode === 'audio' ? 'bg-cyan-600 text-white' : 'bg-[#1a1e27] text-slate-400 border border-[#2a2e39]'}`}>Audio</button>
                            </div>

                            <div className="flex flex-col gap-3">
                                <div>
                                    <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">{omniMode === 'image' ? 'Tool / Model' : 'Tool'}</label>
                                    <select value={omniMode === 'image' ? omniImageModel : omniAudioModel} onChange={e => omniMode === 'image' ? setOmniImageModel(e.target.value) : setOmniAudioModel(e.target.value)} className="w-full bg-[#1a1e27] border border-[#2a2e39] rounded-lg p-2 text-xs">
                                        {(omniMode === 'image' ? OMNIGATE_IMAGE_MODELS : OMNIGATE_AUDIO_MODELS).map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                    </select>
                                    <p className="text-[9px] text-cyan-400/70 mt-0.5">{omniMode === 'image' ? OMNIGATE_IMAGE_MODELS[0].note : 'Audio przez MCP 8766 / text_to_audio'}</p>
                                </div>

                                <div>
                                    <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">Prompt</label>
                                    <textarea value={omniPrompt} onChange={e => setOmniPrompt(e.target.value)} rows={omniMode === 'audio' ? 3 : 4} className="w-full bg-[#1a1e27] border border-[#2a2e39] rounded-lg p-2 text-xs resize-none" />
                                </div>

                                {omniMode === 'image' && (
                                    <>
                                        <div>
                                            <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">Negative Prompt</label>
                                            <textarea value={omniNegative} onChange={e => setOmniNegative(e.target.value)} rows={2} className="w-full bg-[#1a1e27] border border-[#2a2e39] rounded-lg p-2 text-xs resize-none" />
                                        </div>
                                        <div>
                                            <label className="text-[9px] uppercase font-bold text-slate-500 block mb-1">Rozdzielczość</label>
                                            <select disabled={!omniSendResolution} value={omniResolution} onChange={e => setOmniResolution(e.target.value)} className="w-full bg-[#1a1e27] border border-[#2a2e39] rounded p-1.5 text-[11px] disabled:opacity-50">
                                                {RESOLUTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <input type="checkbox" id="omniSendResolution" checked={omniSendResolution} onChange={e => setOmniSendResolution(e.target.checked)} className="bg-[#1a1e27] border-[#2a2e39]" />
                                            <label htmlFor="omniSendResolution" className="text-[10px] text-slate-400 cursor-pointer">Wyślij parametr rozdzielczości (Lexxa generuje z natywną)</label>
                                        </div>
                                        <div className="text-[10px] text-slate-400 bg-[#1a1e27] border border-[#2a2e39] rounded-lg p-2">
                                            Ręczne ustawienia generatora utrzymujesz bezpośrednio w browser preview `7901`.
                                        </div>
                                    </>
                                )}

                                {omniMode === 'audio' && (
                                    <div>
                                        <label className="text-[9px] uppercase font-bold text-slate-500 block mb-1">Voice</label>
                                        <select value={omniVoice} onChange={e => setOmniVoice(e.target.value)} className="w-full bg-[#1a1e27] border border-[#2a2e39] rounded p-1.5 text-[11px]">
                                            {AUDIO_VOICES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                                        </select>
                                    </div>
                                )}

                                <button onClick={handleGenerateOmni} disabled={omniBusy || !omniPrompt.trim()} className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 disabled:opacity-40 text-white font-black text-[11px] uppercase tracking-widest py-3 rounded-xl flex justify-center items-center gap-2">
                                    {omniBusy ? <><i className="fas fa-spinner fa-spin" /><span>WYSYŁANIE...</span></> : <><i className={`fas ${omniMode === 'audio' ? 'fa-music' : 'fa-bolt'}`} /><span>GENERUJ OMNIGATE</span></>}
                                </button>
                            </div>
                        </section>

                        {lastError && (
                            <div className="text-[10px] bg-red-500/10 border border-red-500/30 rounded p-2 text-red-400">{lastError}</div>
                        )}

                        {queueTasks.length > 0 && (
                            <div className="border-t border-[#2a2e39] pt-3 mt-1">
                                <h3 className="text-[9px] uppercase font-bold text-slate-500 mb-2 flex items-center gap-2">
                                    <i className="fas fa-layer-group text-pink-500" /> Live Queue ({queueTasks.length})
                                </h3>
                                <div className="flex flex-col gap-1.5 max-h-40 overflow-y-auto">
                                    {queueTasks.map(task => (
                                        <div key={task.task_id} className="text-[10px] bg-[#131722] border border-[#2a2e39] p-2 rounded-md flex justify-between items-center gap-2">
                                            <span className="truncate flex-1 text-slate-300">{task.prompt || '—'}</span>
                                            <StatusBadge status={task.status} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex-1 p-4 overflow-y-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 content-start">
                        {mediaItems.map(renderMediaCard)}
                        {mediaItems.length === 0 && (
                            <div className="col-span-full py-20 flex flex-col items-center justify-center text-slate-600">
                                <i className="fas fa-photo-film text-5xl mb-4 opacity-20" />
                                <p className="text-[11px] font-bold uppercase tracking-widest">Brak mediów</p>
                                <p className="text-[10px] mt-2 text-slate-600 text-center max-w-xs">
                                    NVIDIA i OmniGate mogą lądować w jednej galerii. Użytkownik nie musi wiedzieć, co jest spodem.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
